import { useEffect, useState } from 'react'
import { getGoldenResults, goldenScoresByFixture, type GoldenFixture } from '../api/dev'
import { isDevToolAvailable } from '../devToolCapabilities'
import type { DataMode } from '../dataMode'

export interface GoldenResultsController {
  /** Every fixture of the gameweek that has a known final score, in fixture order. */
  fixtures: GoldenFixture[]
  /** The same thing keyed by fixture id, which is what the picker asks. */
  byFixture: Record<string, { homeScore: number; awayScore: number }>
}

const EMPTY: GoldenResultsController = { fixtures: [], byFixture: {} }

/**
 * The gameweek's answer sheet, for the dev picker row.
 *
 * **One request per gameweek**, never one per fixture: the picker cycles through every fixture in
 * a week inside a single modal, so this deliberately sits above it.
 *
 * Empty unless the capability says otherwise, which means empty in live. `enabled` is the caller's
 * own extra gate — the route is admin-gated server-side, so anywhere outside the admin-only dev
 * rail has to pass `isAdmin` or it would collect 403s.
 *
 * A null `gameweek` lets the server answer for whichever one is current.
 *
 * What is held in state carries the key it was fetched for, so a gameweek change reads as empty
 * until its own answer lands rather than showing the previous week's scores against this week's
 * fixtures.
 */
export function useGoldenResults({
  mode,
  gameweek,
  enabled: callerEnabled = true,
}: {
  mode: DataMode | null
  gameweek?: number | null
  enabled?: boolean
}): GoldenResultsController {
  const enabled = callerEnabled && isDevToolAvailable('replicaSpoilers', mode)
  const key = `${mode}:${gameweek ?? 'current'}`
  const [loaded, setLoaded] = useState<{ key: string; data: GoldenResultsController } | null>(null)

  useEffect(() => {
    if (!enabled) return
    let live = true
    getGoldenResults(gameweek ?? undefined)
      .then((payload) => {
        if (!live) return
        setLoaded({
          key,
          data: { fixtures: payload.fixtures, byFixture: goldenScoresByFixture(payload) },
        })
      })
      .catch(() => {
        // A dev readout that fails is a missing row, not an error the app should show.
        if (live) setLoaded({ key, data: EMPTY })
      })
    return () => {
      live = false
    }
  }, [enabled, gameweek, key])

  return enabled && loaded?.key === key ? loaded.data : EMPTY
}
